import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { config } from '../../config';
import Navigation from '../../components/Navigation/Navigation';
import AddPostModal from '../../components/AddPostModal/AddPostModal';
import './StoryDetailsPage.css';

interface Story {
  id: string;
  title: string;
  l0_category: string;
  l1_category: string;
  l2_category: string;
  tags: string[];
  author: string;
  duration: number;
  language: string;
  image_url: string;
  is_active: boolean;
  type: string;
  created_at: string;
  updated_at: string;
}

interface StoryPost {
  id: string;
  title: string;
  description: string;
  language: string;
  script: string;
  urls: string[];
  created_at: string;
  updated_at: string;
  tags: string[];
  entity_id: string;
  author_id: string;
  lrc_url: string | null;
  post_type: string;
  duration: number;
  l0_category: string;
  l1_category: string;
  l2_category: string;
  hash_tags: string[] | null;
  characters: any[] | null;
  background_audio_urls: string[];
  interaction_metadata: any[] | null;
  background_color: string;
  text_color: string;
  system_metadata: any | null;
  type: string;
  system_type: string | null;
}

const StoryDetailsPage: React.FC = () => {
  const { storyId } = useParams<{ storyId: string }>();
  const navigate = useNavigate();
  const [story, setStory] = useState<Story | null>(null);
  const [posts, setPosts] = useState<StoryPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddPostModal, setShowAddPostModal] = useState(false);
  const [editingPost, setEditingPost] = useState<StoryPost | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchStoryDetails();
    fetchStoryPosts();
  }, [storyId]);

  const fetchStoryDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${config.API_BASE_URL}/story/${storyId}`, {
        method: 'GET',
        headers: {
          accept: 'application/json',
          ...(token && { 'x-auth-token': token }),
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch story details');
      }

      const data = await response.json();
      setStory(data);
    } catch (error) {
      console.error('Error fetching story details:', error);
      setError('Failed to load story details');
    } finally {
      setLoading(false);
    }
  };

  const fetchStoryPosts = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${config.API_BASE_URL}/story-post-mapper/${storyId}`,
        {
          method: 'GET',
          headers: {
            accept: 'application/json',
            ...(token && { 'x-auth-token': token }),
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch posts');
      }

      const data = await response.json();
      setPosts(data.posts || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
    }
  };

  const handleDeletePost = async (postId: string) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      const token = localStorage.getItem('authToken');
      const headers = {
        accept: 'application/json',
        ...(token && { 'x-auth-token': token }),
      };

      const [mapperRes, feedPostRes] = await Promise.all([
        fetch(`${config.API_BASE_URL}/story-post-mapper/${storyId}/${postId}`, {
          method: 'DELETE',
          headers,
        }),
        fetch(`${config.API_BASE_URL}/feed-post/${postId}`, {
          method: 'DELETE',
          headers,
        }),
      ]);

      if (!mapperRes.ok || !feedPostRes.ok) {
        const errors = [];
        if (!mapperRes.ok) errors.push('mapper delete');
        if (!feedPostRes.ok) errors.push('feed post delete');
        throw new Error(`Failed: ${errors.join(', ')}`);
      }

      alert('Post deleted successfully');
      fetchStoryPosts();
    } catch (error) {
      console.error('Error deleting post:', error);
      alert('Failed to delete post');
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      const token = localStorage.getItem('authToken');
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(
        `${config.API_BASE_URL}/feed-post/bulk-upload`,
        {
          method: 'POST',
          headers: {
            accept: 'application/json',
            ...(token && { 'x-auth-token': token }),
          },
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'bulk-upload-result.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      alert('Upload successful! Result file downloaded.');
      fetchStoryPosts();
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload file');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  if (loading) {
    return (
      <div className="story-details-loading">
        <div className="loading-spinner"></div>
        <span>Loading story details...</span>
      </div>
    );
  }

  if (error || !story) {
    return (
      <div className="story-details-error">
        <p>{error || 'Story not found'}</p>
      </div>
    );
  }

  return (
    <>
      <Navigation showBack={true} />
      <div className="story-details-container">
        <div className="story-details-header">
          <h1>{story.title}</h1>
          <div className="story-header-actions">
            <label className="upload-excel-button">
              {uploading ? 'Uploading...' : 'Upload excel sheet'}
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                disabled={uploading}
                style={{ display: 'none' }}
              />
            </label>
            <button
              className="add-post-button"
              onClick={() => setShowAddPostModal(true)}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Add Post
            </button>
          </div>
        </div>

        <div className="story-details-content">
          <div className="story-posts-section">
            <h3>Posts</h3>
            {posts.length > 0 ? (
              <div className="posts-grid">
                {posts.map(post => (
                  <div key={post.id} className="post-card">
                    <div className="post-content">
                      <h4 className="post-title">{post.title}</h4>
                      {post.description && (
                        <p className="post-description">{post.description}</p>
                      )}
                      <div className="post-meta">
                        <span>
                          ID: {post.id}
                          <svg
                            onClick={() =>
                              navigator.clipboard.writeText(post.id)
                            }
                            style={{
                              cursor: 'pointer',
                              marginLeft: '8px',
                              verticalAlign: 'middle',
                            }}
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <rect
                              x="9"
                              y="9"
                              width="13"
                              height="13"
                              rx="2"
                              ry="2"
                            />
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                          </svg>
                        </span>
                        <span> | Type: {post.post_type}</span>
                        <span> | Language: {post.language}</span>
                      </div>
                    </div>
                    <div className="post-actions">
                      <button
                        className="edit-post-button"
                        onClick={() => setEditingPost(post)}
                      >
                        Edit
                      </button>
                      <button
                        className="delete-post-button"
                        onClick={() => handleDeletePost(post.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="posts-empty">
                <svg
                  width="60"
                  height="60"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h4>No Posts Yet</h4>
                <p>Click "Add Post" to create the first post for this story.</p>
              </div>
            )}
          </div>
        </div>
      </div>
      <AddPostModal
        isOpen={showAddPostModal}
        onClose={() => setShowAddPostModal(false)}
        storyId={storyId!}
        storyData={
          story
            ? {
                l0_category: story.l0_category,
                l1_category: story.l1_category,
                l2_category: story.l2_category,
                language: story.language,
                author: story.author,
              }
            : undefined
        }
        onPostCreated={fetchStoryPosts}
      />
      {editingPost && (
        <AddPostModal
          isOpen={true}
          onClose={() => setEditingPost(null)}
          storyId={storyId!}
          storyData={
            story
              ? {
                  l0_category: story.l0_category,
                  l1_category: story.l1_category,
                  l2_category: story.l2_category,
                  language: story.language,
                  author: story.author,
                }
              : undefined
          }
          onPostCreated={() => {
            setEditingPost(null);
            fetchStoryPosts();
          }}
          editingPost={editingPost}
        />
      )}
    </>
  );
};

export default StoryDetailsPage;
